/**
 * The audit checkpoint on paper — DECISIONS.md #53 step 2, DATA-MODEL.md §12's daily checkpoint.
 *
 * **Nothing is written until the button is pressed.** Producing a sheet records it as printed, and
 * that is what clears the reminder for a day — so it happens when somebody asks to print, and the
 * print dialog opens with it. It used to happen when the screen opened, which let a tap on the
 * reminder and a press of Back clear it with no paper produced, and wrote two rows under React's
 * StrictMode (review of 2026-10-09). A cancelled print dialog cannot be detected; that is the
 * remaining gap, and it is the person at the printer's to close.
 *
 * **Every value as the server sent it.** The seq, the hash and the command are the log's own and are
 * not reformatted — a hash with a space inserted for readability is a different string to whoever
 * types it back. Only the two times go through the shared formatter (AGENTS.md §5).
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import { printCheckpoint, type CheckpointSheet } from './checkpointApi'

/**
 * The command the technician runs, before the server's arguments. AGENTS.md §10's form; a literal and
 * not catalogue text, because it is typed into a shell and is the same in every language.
 */
const VERIFY_COMMAND = 'docker compose -f deploy/compose.yaml exec api dotnet Continuum.Api.dll'

type State =
  | { readonly kind: 'ready' }
  | { readonly kind: 'producing' }
  | { readonly kind: 'loaded'; readonly sheet: CheckpointSheet }
  | { readonly kind: 'failed' }

function clinicalTime(instant: string, unreadable: string): string {
  try {
    return formatClinicalDateTime(instant)
  } catch {
    return unreadable
  }
}

export function CheckpointScreen({ onBack }: { readonly onBack: () => void }) {
  const { t } = useTranslation(['common', 'print', 'checkpoint'])
  const [state, setState] = useState<State>({ kind: 'ready' })

  // Set when a sheet arrives, so the print dialog opens once it is on screen — after the commit, not
  // from inside the response handler, where the sheet is not rendered yet.
  const printWhenShown = useRef(false)

  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const produce = () => {
    if (state.kind === 'producing') {
      return
    }

    setState({ kind: 'producing' })

    printCheckpoint(new AbortController().signal).then(
      (sheet) => {
        if (live.current) {
          printWhenShown.current = true
          setState({ kind: 'loaded', sheet })
        }
      },
      () => {
        if (live.current) {
          setState({ kind: 'failed' })
        }
      },
    )
  }

  useEffect(() => {
    if (state.kind === 'loaded' && printWhenShown.current) {
      printWhenShown.current = false
      window.print()
    }
  }, [state])

  const unreadable = t('common:value.unreadable')

  return (
    <main className="print-screen">
      <div className="print-screen__actions">
        {/* Its own words: this screen is reached from the ward list, not from a patient. */}
        <button type="button" className="print-screen__back" onClick={onBack}>
          {t('checkpoint:sheet.back')}
        </button>

        {/* Reprinting the same sheet is the browser's print again and writes no new row. */}
        {state.kind === 'loaded' && (
          <button type="button" className="print-screen__print" onClick={() => window.print()}>
            {t('print:action.print')}
          </button>
        )}
      </div>

      <div aria-live="polite">
        {(state.kind === 'ready' || state.kind === 'producing') && (
          <div className="print-screen__notice">
            <h1>{t('checkpoint:sheet.title')}</h1>
            <p>{t('checkpoint:sheet.intro')}</p>
            <button
              type="button"
              className="print-screen__print"
              disabled={state.kind === 'producing'}
              onClick={produce}
            >
              {state.kind === 'producing' ? t('checkpoint:sheet.producing') : t('checkpoint:sheet.produce')}
            </button>
          </div>
        )}

        {state.kind === 'failed' && (
          <div className="print-screen__notice print-screen__notice--error" role="alert">
            <p>{t('checkpoint:sheet.failed')}</p>
            <button type="button" onClick={produce}>
              {t('checkpoint:sheet.retry')}
            </button>
          </div>
        )}
      </div>

      {state.kind === 'loaded' && (
        <article className="print-document print-checkpoint">
          <header className="print-document__header">
            <p className="print-document__source">{t('print:document.source')}</p>
            <h1 className="print-document__title">{t('checkpoint:sheet.title')}</h1>
            <p>{t('checkpoint:sheet.explanation')}</p>
          </header>

          <dl className="print-checkpoint__fields">
            <dt>{t('checkpoint:sheet.seq')}</dt>
            <dd className="print-checkpoint__value">{state.sheet.seq}</dd>

            <dt>{t('checkpoint:sheet.hash')}</dt>
            <dd className="print-checkpoint__value print-checkpoint__hash">{state.sheet.hash}</dd>

            <dt>{t('checkpoint:sheet.fingerprint')}</dt>
            <dd className="print-checkpoint__value">{state.sheet.fingerprint}</dd>

            <dt>{t('checkpoint:sheet.anchoredAt')}</dt>
            <dd>{clinicalTime(state.sheet.anchoredAt, unreadable)}</dd>

            <dt>{t('checkpoint:sheet.printedAt')}</dt>
            <dd>{clinicalTime(state.sheet.printedAt, unreadable)}</dd>
          </dl>

          <p>{t('checkpoint:sheet.notVerified')}</p>

          <section className="print-checkpoint__command">
            <h2>{t('checkpoint:sheet.commandHeading')}</h2>
            <p>{t('checkpoint:sheet.commandExplanation')}</p>
            <pre className="print-checkpoint__value">
              {VERIFY_COMMAND} {state.sheet.verifyArguments}
            </pre>
            <p>{t('checkpoint:sheet.fingerprintNote')}</p>
          </section>
        </article>
      )}
    </main>
  )
}
